import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE, STANDARDS } from "../data/site";

// Document-head manager: title, meta description, robots (environment-based),
// canonical, Open Graph, Twitter card, JSON-LD.
// REACT_APP_ROBOTS: "noindex, nofollow" on staging, "index, follow" in production.
const ROBOTS = process.env.REACT_APP_ROBOTS || "noindex, nofollow";

// Single source of truth for the structured-data postal address
// (owner-approved wording, 2026-08-30).
export const POSTAL_ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: "Office No. F-407, F-Wing, 4th Floor, Tower II, L&T Seawoods Grand Central, Plot No. R-1, Sector 40, Seawoods Railway Station, Nerul",
  addressLocality: "Navi Mumbai",
  addressRegion: "Maharashtra",
  postalCode: "400706",
  addressCountry: "IN",
};

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE.name,
    // legalName omitted until the registered entity name is confirmed by the owner
    url: SITE.url,
    logo: `${SITE.url}/assets/dcc-logo.webp`,
    description: SITE.description,
    telephone: SITE.contact.phone,
    email: SITE.contact.email,
    address: POSTAL_ADDRESS,
    areaServed: { "@type": "Country", name: "India" },
    // memberships confirmed by owner — referenced standards are NOT listed here
    memberOf: STANDARDS.filter((s) => s.relation === "Member").map((s) => ({ "@type": "Organization", name: s.name })),
    // sameAs carries only genuine, verified official URLs — the Google Maps
    // place profile (owner-supplied) plus socials once their URLs are supplied
    ...(SITE.social.linkedin || SITE.social.instagram || SITE.contact.mapsUrl
      ? { sameAs: [SITE.social.linkedin, SITE.social.instagram, SITE.contact.mapsUrl].filter(Boolean) }
      : {}),
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE.url}${it.path}`,
    })),
  };
}

export function SEO({ title, description, path, type = "website", jsonLd, image, robots }) {
  const location = useLocation();
  const pathname = path || location.pathname;
  const canonical = `${SITE.url}${pathname === "/" ? "" : pathname}`;
  const fullTitle = title || `${SITE.name} — Specialist Commercial Duct Cleaning, India`;
  const desc = description || SITE.tagline;
  const ogImage = image ? (image.startsWith("http") ? image : `${SITE.url}${image}`) : `${SITE.url}/assets/og-default.jpg`;

  useEffect(() => {
    document.title = fullTitle;
    const setMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("name", "description", desc);
    setMeta("name", "robots", robots || ROBOTS);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:type", type);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:site_name", SITE.name);
    setMeta("property", "og:image", ogImage);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", ogImage);
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);

    document.head.querySelectorAll("script[data-seo-jsonld]").forEach((n) => n.remove());
    const schemas = [
      organizationSchema(),
      ...(jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []),
    ];
    schemas.forEach((schema) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-jsonld", "true");
      s.textContent = JSON.stringify(schema);
      document.head.appendChild(s);
    });
  }, [fullTitle, desc, canonical, type, ogImage, robots, jsonLd]);

  return null;
}
